import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import * as dashboardBuilderService from '../../../../services/dashboardBuilder';

const DashboardDataContext = createContext(null);

export const DashboardDataProvider = ({ children }) => {
  const [state, setState] = useState({
    dashboards: [],
    templates: [],
    sections: [],
    templateCategories: [],
    loadingDashboards: false,
    loadingTemplates: false,
    loadingSections: false,
    error: null,
  });

  const updateState = useCallback((updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const fetchDashboards = useCallback(
    async (filters = {}) => {
      updateState({ loadingDashboards: true, error: null });
      try {
        const response = await dashboardBuilderService.getDashboards(filters);
        updateState({
          dashboards: Array.isArray(response.body) ? response.body : [],
          loadingDashboards: false,
        });
      } catch (err) {
        console.error('Error fetching dashboards:', err);
        updateState({
          loadingDashboards: false,
          error: err,
          dashboards: [],
        });
      }
    },
    [updateState]
  );

  const fetchTemplates = useCallback(
    async (filters = {}) => {
      updateState({ loadingTemplates: true });
      try {
        const response = await dashboardBuilderService.getTemplates(filters);
        updateState({
          templates: Array.isArray(response.body) ? response.body : [],
          loadingTemplates: false,
        });
      } catch (err) {
        console.error('Error fetching templates:', err);
        updateState({ loadingTemplates: false });
      }
    },
    [updateState]
  );

  const fetchSections = useCallback(async () => {
    updateState({ loadingSections: true });
    try {
      const response = await dashboardBuilderService.getAvailableSections();
      updateState({
        sections: Array.isArray(response.body) ? response.body : [],
        loadingSections: false,
      });
    } catch (err) {
      console.error('Error fetching sections:', err);
      updateState({ loadingSections: false });
    }
  }, [updateState]);

  const fetchTemplateCategories = useCallback(async () => {
    try {
      const response = await dashboardBuilderService.getTemplateCategories();
      updateState({
        templateCategories: Array.isArray(response.body) ? response.body : [],
      });
    } catch (err) {
      console.error('Error fetching template categories:', err);
    }
  }, [updateState]);

  const value = useMemo(
    () => ({
      dashboards: state.dashboards,
      templates: state.templates,
      sections: state.sections,
      templateCategories: state.templateCategories,
      loadingDashboards: state.loadingDashboards,
      loadingTemplates: state.loadingTemplates,
      loadingSections: state.loadingSections,
      error: state.error,
      fetchDashboards,
      fetchTemplates,
      fetchSections,
      fetchTemplateCategories,
    }),
    [
      state.dashboards,
      state.templates,
      state.sections,
      state.templateCategories,
      state.loadingDashboards,
      state.loadingTemplates,
      state.loadingSections,
      state.error,
      fetchDashboards,
      fetchTemplates,
      fetchSections,
      fetchTemplateCategories,
    ]
  );

  return (
    <DashboardDataContext.Provider value={value}>
      {children}
    </DashboardDataContext.Provider>
  );
};

export const useDashboardData = () => {
  const context = useContext(DashboardDataContext);

  if (!context) {
    throw new Error(
      'useDashboardData must be used within DashboardDataProvider'
    );
  }

  return context;
};
